import React, { useEffect } from "react";
import { Typography } from "@/components/ui/Typography/Typography";
import { Button } from "@/components/ui/Button";
import { Badge } from "@/components/ui/Badge/badge";
import {
  useGetRoleByIdQuery,
  useGetRoleUsageInfoQuery,
} from "@/api/role-management/role-management.hooks";
import {
  TEnhancedRole,
  TRole,
} from "@/api/role-management/role-management.types";
import { useTranslation } from "react-i18next";
import KpiLoaders from "@/pages/layout/loaders/KpiLoaders";
import { CommonCardUI } from "@/components/ui";
import {
  CircleAlert,
  Dot,
  Edit,
  KeyRound,
  MoveLeft,
  Settings,
  UserRoundCheck,
} from "lucide-react";
import { usePermissions } from "@/hooks/usePermissions";
import { PermissionGuard } from "@/components/PermissionGuard";
import { formatLocaleDate } from "@/utils/commonUtils";

type TRoleDetailProps = {
  role: TRole;
  onEdit: () => void;
  onRoleSelect: (role: TEnhancedRole, from: string) => void;
  onBack: (role: TEnhancedRole, from: string) => void;
};

export const RoleDetail: React.FC<TRoleDetailProps> = ({
  role,
  onEdit,
  onBack,
  onRoleSelect,
}) => {
  const { t } = useTranslation(["roleManagementPage", "common"]);
  const { hasPermission } = usePermissions();

  const {
    data: roleDetail,
    isLoading,
    refetch: refetchRoleDetail, // 👈 here it comes from the hook
  } = useGetRoleByIdQuery(role.internal_id, {
    enabled: !!role.internal_id && hasPermission("can-view-role-groups"),
  });

  const { data: usageInfo } = useGetRoleUsageInfoQuery(role.id, {
    enabled: hasPermission("can-view-role-groups"),
  });

  useEffect(() => {
    if (role?.internal_id) void refetchRoleDetail();
  }, [role?.internal_id, refetchRoleDetail]);

  if (isLoading) {
    return (
      <>
        <div className="md:col-span-12">
          <div className="grid grid-cols-12 items-center gap-4">
            <div className="col-span-8 flex items-center gap-4">
              <KpiLoaders numberOfLoaders={1} className="w-8 h-8" />
              <div className="space-y-2">
                <KpiLoaders numberOfLoaders={1} className="w-32 h-6" />
                <KpiLoaders numberOfLoaders={1} className="w-64 h-4" />
              </div>
            </div>

            <div className="col-span-4 flex justify-end">
              <KpiLoaders numberOfLoaders={2} className="w-32 h-6 m-2" />
            </div>
          </div>
        </div>

        <div className="md:col-span-12 grid grid-cols-1 md:grid-cols-3 gap-6 w-full">
          <div className="w-full rounded-lg overflow-hidden">
            <KpiLoaders numberOfLoaders={1} className="w-full h-[90px]" />
          </div>
          <div className="w-full rounded-lg overflow-hidden">
            <KpiLoaders numberOfLoaders={1} className="w-full h-[90px]" />
          </div>
          <div className="w-full rounded-lg overflow-hidden">
            <KpiLoaders numberOfLoaders={1} className="w-full h-[90px]" />
          </div>
        </div>
        <div>
          <KpiLoaders numberOfLoaders={1} className="w-full h-[100px]" />
        </div>
        <div>
          <KpiLoaders numberOfLoaders={1} className="w-full h-[100px]" />
        </div>
      </>
    );
  }

  const permissions = roleDetail?.permissions ?? [];

  // KPI
  const renderRoleKpiCard = (
    type: "permissions" | "active_users" | "automation_rules",
    count: number,
    title: string,
  ) => {
    const kpiConfig = {
      permissions: {
        icon: <KeyRound size={20} />,
        iconColor: "text-blue-600",
        countColor: "text-gray-900",
        titleColor: "text-foreground",
      },
      active_users: {
        icon: <UserRoundCheck size={20} />,
        iconColor: "text-green-600",
        countColor: "text-gray-900",
        titleColor: "text-foreground",
      },
      automation_rules: {
        icon: <Settings size={20} />,
        iconColor: "text-pink-600",
        countColor: "text-gray-900",
        titleColor: "text-foreground",
      },
    };

    const { icon, titleColor, iconColor } = kpiConfig[type];

    return (
      <CommonCardUI
        className="custom-bx-shadow bg-background transition hover:-translate-y-1 hover:shadow-xl"
        classNameContent="p-4"
      >
        <div className="flex items-center justify-between">
          <div>
            <Typography
              variant={"p"}
              affects={"semiBold"}
              className="text-gray text-left"
            >
              {title}
            </Typography>
            <Typography variant="h4" affects={"bold"} className={titleColor}>
              {count}
            </Typography>
          </div>
          <div className={iconColor}>{icon}</div>
        </div>
      </CommonCardUI>
    );
  };
  return (
    <div className="space-y-6">
      <CommonCardUI
        classNameContent="p-3"
        className="custom-bx-shadow  bg-background gap-3"
      >
        <div className="flex items-center justify-between gap-2">
          <div className="flex flex-col gap-1">
            <div className="flex items-center gap-2">
              <Typography affects={"semiBold"} className="mt-0" variant="h5">
                {roleDetail?.name ?? ""}
              </Typography>
              <Badge
                variant={roleDetail?.is_active ? "green" : "gray"}
                size="sm"
              >
                {roleDetail?.is_active
                  ? t("GLOBAL.ACTIVE")
                  : t("GLOBAL.INACTIVE")}
              </Badge>
            </div>
            <div className="flex items-center gap-1">
              <span className="text-gray-600 dark:text-gray-400 text-sm">
                {t("ROLES.ROLE_DETAILS.HIERARCHY_LEVEL")} :{" "}
                {roleDetail?.rank ?? t("common:N_A")}
              </span>
              <Dot size={14} />
              <span className="text-gray-600 dark:text-gray-400 text-sm">
                {t("ROLES.ROLE_DETAILS.CREATED")} :{" "}
                {roleDetail?.audit_trail?.created_on
                  ? formatLocaleDate(
                      new Date(roleDetail.audit_trail.created_on),
                    )
                  : t("common:N_A")}
              </span>
              <Dot size={14} />
              <span className="text-gray-600 dark:text-gray-400 text-sm">
                {t("ROLES.ROLE_DETAILS.LAST_UPDATED")} :{" "}
                {roleDetail?.audit_trail?.updated_on
                  ? formatLocaleDate(
                      new Date(roleDetail.audit_trail.updated_on),
                    )
                  : t("common:N_A")}
              </span>
            </div>
          </div>
          <div className="flex items-center space-x-3">
            <Button
              variant="ghost"
              size="sm"
              type="button"
              className="flex items-center gap-2 min-w-[120px]"
              onClick={() => onBack(roleDetail as TEnhancedRole, "view")}
            >
              <MoveLeft size={16} />
              {t("ROLES.BUTTONS.BACK_TO_LIST")}
            </Button>
            <Button
              variant="default"
              size="sm"
              className="flex items-center gap-2 min-w-[110px] font-medium"
              onClick={() => onEdit()}
            >
              <Edit size={16} />
              {t("ROLES.BUTTONS.EDIT_ROLE")}
            </Button>
          </div>
        </div>
      </CommonCardUI>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {renderRoleKpiCard(
          "permissions",
          roleDetail?.permission_count ?? 0,
          t("ROLES.ROLE_DETAILS.KPI.PERMISSIONS"),
        )}

        {renderRoleKpiCard(
          "active_users",
          roleDetail?.assignment_statistics?.active_assignments ?? 0,
          t("ROLES.ROLE_DETAILS.KPI.ACTIVE_USERS"),
        )}

        {renderRoleKpiCard(
          "automation_rules",
          usageInfo?.automation_rules_count ?? 0,
          t("ROLES.ROLE_DETAILS.KPI.AUTOMATION_RULES"),
        )}
      </div>

      <CommonCardUI
        classNameContent="flex items-start gap-2"
        className={`custom-bx-shadow transition-colors
          ${
            usageInfo?.can_delete
              ? "bg-green/05 border border-green/10 border-l-4 border-l-green"
              : "bg-destructive/05 border border-destructive/10 border-l-4 border-l-destructive"
          }
        `}
      >
        <CircleAlert
          size={18}
          className={`mt-0.5 ${
            usageInfo?.can_delete ? "text-green" : "text-destructive"
          }`}
        />

        <div>
          <Typography
            variant="h6"
            affects="semiBold"
            className={`${usageInfo?.can_delete ? "text-green" : "text-destructive"}`}
          >
            {t("ROLES.ROLE_DETAILS.DELETION_STATUS")}
          </Typography>

          <Typography
            variant="p"
            className={`${
              usageInfo?.can_delete ? "text-green" : "text-destructive"
            }`}
          >
            {t("ROLES.ROLE_DETAILS.DELETION_MESSAGE")}
          </Typography>
        </div>
      </CommonCardUI>
      {usageInfo?.warning_message && (
        <CommonCardUI
          classNameContent="flex items-start gap-2"
          className="custom-bx-shadow bg-yellow-50 border border-l-4 dark:border-yellow-600 border-yellow-300"
        >
          <CircleAlert
            size={18}
            className="mt-0.5 text-yellow-800 dark:text-yellow-400"
          />
          <div>
            <Typography
              variant="h6"
              affects="semiBold"
              className="text-yellow-800 dark:text-yellow-600"
            >
              {t("ROLES.ROLE_DETAILS.WARNING")}
            </Typography>
            <Typography
              variant="p"
              className="text-yellow-800 dark:text-yellow-600"
            >
              {usageInfo?.warning_message ??
                t("ROLES.ROLE_DETAILS.DELETION_MESSAGE")}
            </Typography>
          </div>
        </CommonCardUI>
      )}

      {/* Permissions List */}
      <CommonCardUI
        classNameContent="p-0"
        className="custom-bx-shadow  bg-background gap-3"
      >
        <div className="flex items-center justify-between border-b border-dashed p-3">
          <Typography
            affects={"semiBold"}
            className="mt-0"
            data-testid="labelTestId"
          >
            {t("ROLES.ROLE_DETAILS.ASSIGNED_PERMISSIONS")} (
            {permissions?.length})
          </Typography>
          {permissions?.length > 0 && (
            <PermissionGuard permissions={["can-view-permissions"]}>
              <Button
                variant="link"
                size="sm"
                onClick={() =>
                  onRoleSelect(roleDetail as TEnhancedRole, "view")
                }
                className="text-primary"
              >
                {t("ROLES.ROLE_DETAILS.MANAGE_PERMISSIONS")}
              </Button>
            </PermissionGuard>
          )}
        </div>
        <div className="p-3">
          {permissions?.length > 0 ? (
            <div className="flex flex-wrap gap-2">
              {permissions?.map((permission) => (
                <div key={permission?.id}>
                  <Badge
                    variant="outline"
                    className="text-sm font-medium text-gray-800 dark:text-gray-200 px-3 py-1 bg-gray-100 dark:bg-gray-700 dark:text-gray-300"
                  >
                    {permission?.name}
                  </Badge>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8  rounded-lg border  dark:bg-background">
              <Typography variant="p" className="text-gray-500 ">
                {/* No permissions assigned to this role */}
                {t("ROLES.ROLE_DETAILS.NO_ASSIGNED_PERMISSIONS")}
              </Typography>
              <PermissionGuard permissions={["can-view-permissions"]}>
                <Button
                  variant="outline"
                  size="sm"
                  className="mt-3"
                  onClick={() =>
                    onRoleSelect(roleDetail as TEnhancedRole, "view")
                  }
                >
                  {t("ROLES.ROLE_DETAILS.ASSIGN_PERMISSIONS")}
                </Button>
              </PermissionGuard>
            </div>
          )}
        </div>
      </CommonCardUI>
    </div>
  );
};
